import React, { createContext, useContext, useEffect, useState } from "react";
import { User } from "firebase/auth";
import {
  auth,
  checkAuthState,
  registerWithEmail,
  loginWithEmail,
  logout,
  saveUserProfile,
  getUserProfile,
  checkEmailExists,
} from "../firebase/firebase";
import {
  startKakaoLogin,
  handleKakaoCallback,
  startNaverLogin,
  handleNaverCallback,
  loginWithNaver,
  logoutFromSocialProvider,
  signupWithKakao,
  signupWithNaver,
} from "../services/socialAuth";

// 컨텍스트 타입 정의
interface AuthContextType {
  currentUser: User | null;
  userProfile: any;
  loading: boolean;
  signUp: (email: string, password: string, name: string) => Promise<User>;
  signIn: (email: string, password: string) => Promise<User>;
  signInWithKakao: () => void;
  handleKakaoLoginCallback: () => Promise<any>;
  signUpWithKakao: (signupData: any) => Promise<any>;
  signInWithNaver: () => void;
  handleNaverLoginCallback: () => Promise<any>;
  signUpWithNaver: (signupData: any) => Promise<any>;
  signOut: () => Promise<void>;
  checkEmail: (email: string) => Promise<boolean>;
  updateUserProfile: (
    userId: string,
    name: string,
    phone: string,
    gender: string,
    birthdate: string,
    acceptNotifications: boolean
  ) => Promise<boolean>;
}

// 초기값 설정
const initialAuthState: AuthContextType = {
  currentUser: null,
  userProfile: null,
  loading: true,
  signUp: async () => {
    throw new Error("Not implemented");
  },
  signIn: async () => {
    throw new Error("Not implemented");
  },
  signOut: async () => {
    throw new Error("Not implemented");
  },
  checkEmail: async () => {
    throw new Error("Not implemented");
  },
  updateUserProfile: async () => {
    throw new Error("Not implemented");
  },
  signInWithKakao: () => {
    throw new Error("Not implemented");
  },
  signInWithNaver: () => {
    throw new Error("Not implemented");
  },
  signUpWithKakao: async () => {
    throw new Error("Not implemented");
  },
  handleKakaoLoginCallback: async () => {
    throw new Error("Not implemented");
  },
  handleNaverLoginCallback: async () => {
    throw new Error("Not implemented");
  },
  signUpWithNaver: async () => {
    throw new Error("Not implemented");
  },
};

// 컨텍스트 생성
const AuthContext = createContext<AuthContextType>(initialAuthState);

// 컨텍스트 Provider 컴포넌트
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // 사용자 프로필 불러오기
  const fetchUserProfile = async (user: User) => {
    try {
      const profile = await getUserProfile(user.uid);
      setUserProfile(profile);
    } catch (error) {
      console.error("사용자 프로필 불러오기 오류:", error);
    }
  };

  useEffect(() => {
    // 인증 상태 변경 리스너 설정
    const unsubscribe = checkAuthState((user) => {
      setCurrentUser(user);

      if (user) {
        fetchUserProfile(user);
      } else {
        setUserProfile(null);
      }

      setLoading(false);
    });

    // 컴포넌트 언마운트 시 리스너 제거
    return () => unsubscribe();
  }, []);

  // 회원가입 함수
  const signUp = async (email: string, password: string, name: string) => {
    const user = await registerWithEmail(email, password, name);
    return user;
  };

  // 로그인 함수
  const signIn = async (email: string, password: string) => {
    const user = await loginWithEmail(email, password);
    return user;
  };

  // 로그아웃 함수
  const signOut = async () => {
    try {
      // 현재 사용자의 소셜 로그인 제공자 확인
      if (currentUser && userProfile?.provider) {
        await logoutFromSocialProvider(userProfile.provider);
      }
      await logout();
    } catch (error) {
      console.error("로그아웃 중 오류:", error);
      throw error;
    }
  };

  // 이메일 중복 체크
  const checkEmail = async (email: string) => {
    try {
      console.log("AuthContext checkEmail 호출:", email);
      const result = await checkEmailExists(email);
      console.log("AuthContext checkEmail 결과:", result);
      return result;
    } catch (error) {
      console.error("AuthContext 이메일 체크 오류:", error);
      // 오류 발생 시 안전하게 true 반환 (중복으로 처리)
      return true;
    }
  };

  // 사용자 프로필 업데이트
  const updateUserProfile = async (
    userId: string,
    name: string,
    phone: string,
    gender: string,
    birthdate: string,
    acceptNotifications: boolean
  ) => {
    // 최대 시도 횟수
    const maxAttempts = 3;

    for (let attempts = 1; attempts <= maxAttempts; attempts++) {
      try {
        console.log(`프로필 저장 시도 ${attempts}/${maxAttempts}`);

        const result = await saveUserProfile(
          userId,
          name,
          phone,
          gender,
          birthdate,
          acceptNotifications
        );

        if (result) {
          console.log("프로필 저장 성공");
          setUserProfile({
            name,
            phone,
            gender,
            birthdate,
            acceptNotifications,
          });
        }

        return result;
      } catch (error) {
        console.error(`프로필 저장 시도 ${attempts} 실패:`, error);

        // 마지막 시도가 아니면 잠시 대기 후 재시도
        if (attempts < maxAttempts) {
          await new Promise((resolve) => setTimeout(resolve, 1000 * attempts)); // 점진적 대기
          continue;
        }

        // 모든 시도 실패
        console.error("프로필 저장 최대 시도 횟수 초과");
        return false;
      }
    }

    return false;
  };

  // 카카오 로그인 시작
  const signInWithKakao = () => {
    startKakaoLogin(); // 리다이렉트 실행 (반환값 없음)
  };

  // 카카오 리다이렉트 콜백 처리
  const handleKakaoLoginCallback = async () => {
    try {
      const result = await handleKakaoCallback();

      if (result.requiresSignup) {
        // 회원가입이 필요한 경우
        return {
          requiresSignup: true,
          kakaoInfo: result.kakaoInfo,
          message: result.message,
        };
      }

      if (result.success && result.user) {
        // 로그인 성공
        return result.user;
      }

      throw new Error("카카오 로그인에 실패했습니다.");
    } catch (error: any) {
      console.error("카카오 로그인 콜백 오류:", error);
      throw error;
    }
  };

  // 네이버 로그인 시작
  const signInWithNaver = () => {
    startNaverLogin(); // 리다이렉트 실행 (반환값 없음)
  };

  // 네이버 리다이렉트 콜백 처리
  const handleNaverLoginCallback = async () => {
    try {
      const result = await handleNaverCallback();

      if (result.requiresSignup) {
        // 회원가입이 필요한 경우
        return {
          requiresSignup: true,
          naverInfo: result.naverInfo,
          message: result.message,
        };
      }

      if (result.success && result.user) {
        // 로그인 성공
        return result.user;
      }

      throw new Error("네이버 로그인에 실패했습니다.");
    } catch (error: any) {
      console.error("네이버 로그인 콜백 오류:", error);
      throw error;
    }
  };

  // 카카오 회원가입
  const signUpWithKakao = async (signupData: any) => {
    try {
      const result = await signupWithKakao(signupData);

      if (result.success && result.user) {
        return result.user;
      }

      throw new Error("카카오 회원가입에 실패했습니다.");
    } catch (error: any) {
      console.error("카카오 회원가입 오류:", error);
      throw error;
    }
  };

  // 네이버 회원가입
  const signUpWithNaver = async (signupData: any) => {
    try {
      const result = await signupWithNaver(signupData);

      if (result.success && result.user) {
        return result.user;
      }

      throw new Error("네이버 회원가입에 실패했습니다.");
    } catch (error: any) {
      console.error("네이버 회원가입 오류:", error);
      throw error;
    }
  };

  // 컨텍스트 값 제공
  const value: AuthContextType = {
    currentUser,
    userProfile,
    loading,
    signUp,
    signIn,
    signOut,
    checkEmail,
    updateUserProfile,
    signInWithKakao,
    signInWithNaver,
    signUpWithKakao,
    handleKakaoLoginCallback,
    handleNaverLoginCallback,
    signUpWithNaver,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// 커스텀 훅으로 컨텍스트 사용 간소화
export const useAuth = () => useContext(AuthContext);

export default AuthContext;
